import PropTypes from "prop-types";
import { cloneElement, memo } from "react";
import { Flex, Box, Center, useColorModeValue } from "@chakra-ui/react";
import { useTheme } from "@chakra-ui/react";
import MotionBox from "~/components/MotionBox";
import tinycolor from "tinycolor2";

Stepper.propTypes = {
  direction: PropTypes.string,
  currentStep: PropTypes.number,
  stepSize: PropTypes.string,
  theme: PropTypes.object,
};

Stepper.defaultProps = {
  direction: "row",
  currentStep: -1,
  stepSize: "20",
  theme: null,
};

// Provides a navigation cue to know in which step the user is currently at in a wizard like form.
// The icons for the stepper should be provided as children.
function Stepper(props) {
  const { direction, currentStep, stepSize, theme } = props;
  const chakraTheme = useTheme();

  const CURRENT_STEP_COLOR = useColorModeValue(
    chakraTheme.colors.light.background.superLite,
    chakraTheme.colors.dark.background.superLite
  );
  const CURRENT_STEP_BACKGROUND = useColorModeValue(
    chakraTheme.colors.light.brand.strong,
    chakraTheme.colors.dark.brand.strong
  );
  const COMPLETE_STEP_COLOR = useColorModeValue(
    chakraTheme.colors.light.background.superLite,
    chakraTheme.colors.dark.background.superLite
  );
  const COMPLETE_STEP_BACKGROUND = chakraTheme.colors.feel.positive;
  const PENDING_STEP_COLOR = useColorModeValue(
    chakraTheme.colors.light.brand.strong,
    chakraTheme.colors.dark.brand.strong
  );
  const PENDING_STEP_BACKGROUND = useColorModeValue(
    chakraTheme.colors.light.background.action,
    chakraTheme.colors.dark.background.action
  );
  const SEPARATOR_COLOR = useColorModeValue(
    chakraTheme.colors.light.background.action,
    chakraTheme.colors.dark.background.action
  );

  return (
    <Flex
      width="100%"
      height="100%"
      direction={direction}
      px={direction === "column" ? "2" : "0"}
      py={direction === "row" ? "2" : "0"}
      justify="space-between"
      align="space-between"
    >
      <BuildAllSteps>{props.children}</BuildAllSteps>
    </Flex>
  );

  function BuildAllSteps(props) {
    if (props.children)
      return props.children.map((child, index) =>
        BuildStep(child, index, props.children.length)
      );
    else return null;
  }

  function BuildStep(step, stepIndex, stepCount) {
    if (stepIndex + 1 < stepCount)
      return (
        <Flex direction={direction} flex="1" key={stepIndex} align="center">
          <Box>
            <Step index={stepIndex}>{step}</Step>
          </Box>
          <StepSeparator />
        </Flex>
      );
    else
      return (
        <Step key={stepIndex} index={stepIndex}>
          {step}
        </Step>
      );
  }

  function Step(props) {
    let { index } = props;
    let stepBackgroundVariants = {
      complete: { backgroundColor: COMPLETE_STEP_BACKGROUND, scale: 1.0 },
      current: {
        backgroundColor: theme
          ? theme.colors.brand.strong
          : CURRENT_STEP_BACKGROUND,
        scale: [1.0, 1.1, 1.0],
      },
      pending: {
        backgroundColor: theme
          ? theme.colors.background.strong
          : PENDING_STEP_BACKGROUND,
        scale: 1.0,
      },
    };

    return (
      <MotionBox
        borderRadius="full"
        variants={stepBackgroundVariants}
        initial="pending"
        animate={getStepBackground(index)}
      >
        <Center p="3" boxSize="12">
          {cloneElement(props.children, {
            color: getStepColor(index),
            size: stepSize,
          })}
        </Center>
      </MotionBox>
    );
  }

  function StepSeparator() {
    if (direction === "row") return HorizontalSeparator();
    else return VerticalSeparator();
  }

  function VerticalSeparator() {
    return (
      <Box
        my="2"
        flex="1"
        width="2px"
        backgroundColor={
          theme ? theme.colors.background.strong : SEPARATOR_COLOR
        }
      />
    );
  }

  function HorizontalSeparator() {
    return (
      <Box
        mx="2"
        flex="1"
        height="2px"
        backgroundColor={
          theme ? theme.colors.background.strong : SEPARATOR_COLOR
        }
      />
    );
  }

  function getStepColor(index) {
    if (index + 1 < currentStep) {
      return COMPLETE_STEP_COLOR;
    } else if (index + 1 === currentStep) {
      return theme
        ? tinycolor
            .mostReadable(theme.colors.brand.strong, [theme.colors.text], {
              includeFallbackColors: true,
            })
            .toHexString()
        : CURRENT_STEP_COLOR;
    } else {
      return theme
        ? tinycolor
            .mostReadable(theme.colors.background.strong, [theme.colors.text], {
              includeFallbackColors: true,
            })
            .toHexString()
        : PENDING_STEP_COLOR;
    }
  }

  function getStepBackground(index) {
    if (index + 1 < currentStep) {
      return "complete";
    } else if (index + 1 === currentStep) {
      return "current";
    } else {
      return "pending";
    }
  }
}

// We use the 'memo' technique to avoid re-renderings when the current step hasn't changed
const shouldAvoidReRender = (prevProps, nextProps) =>
  prevProps.currentStep === nextProps.currentStep;

export default memo((props) => <Stepper {...props} />, shouldAvoidReRender);
